import React, { useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import classNames from "classnames";

import { Maybe } from "fond/types";

import { DialogActions, DialogContent, MainContainer, Slide, SlidePagination, StepImage, TextButton } from "./BasicTour.styles";

export type Slides = Array<{
  image: Maybe<React.ReactNode>;
  title: string;
  content: React.ReactNode;
}>;

interface IProps {
  fitContent?: boolean;
  onComplete(): void;
  skipText?: string;
  slides: Slides;
  name: string;
}

const BasicTour: React.FC<IProps> = ({ fitContent, onComplete, slides, name, skipText = "Skip, I'll explore on my own" }) => {
  const [isOpen, setOpen] = useState<boolean>(true);
  const [activeSlide, setActiveSlide] = useState<number>(0);

  const closeDialog = () => {
    onComplete();
    setOpen(false);
  };

  return (
    <Dialog open={isOpen} maxWidth={false}>
      <MainContainer data-testid="tour" className={classNames({ fitContent })}>
        <DialogContent>
          {slides
            .filter((slide, index) => !fitContent || index === activeSlide)
            .map((slide, index) => {
              const slideIndex = fitContent ? activeSlide : index;
              return (
                <Slide key={slide.title} active={slideIndex === activeSlide} className="slide">
                  {slide.image && (typeof slide.image === "string" ? <StepImage image={slide.image} /> : slide.image)}
                  <Box>
                    <Typography variant="h5" component="h3" fontWeight={700} gutterBottom data-testid={`${name}-tour-title`}>
                      {slide.title}
                    </Typography>
                    <br />
                    {slide.content}
                  </Box>
                </Slide>
              );
            })}
        </DialogContent>
        <DialogActions>
          <Box paddingLeft={4} display="flex">
            {slides.map((slide, index) => (
              <SlidePagination key={slide.title} onClick={() => setActiveSlide(index)} active={index === activeSlide} />
            ))}
          </Box>
          <Box>
            {activeSlide === slides.length - 1 ? (
              <Button variant="contained" onClick={() => closeDialog()} data-testid="tour-close">
                Get Started
              </Button>
            ) : (
              <>
                <TextButton variant="text" onClick={() => closeDialog()} sx={{ marginRight: 2 }} data-testid="tour-skip">
                  {skipText}
                </TextButton>
                <Button variant="contained" onClick={() => setActiveSlide(activeSlide + 1)} data-testid="tour-next">
                  Continue
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </MainContainer>
    </Dialog>
  );
};
export default BasicTour;
